import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { AxiosRequestHeaders } from 'axios';
import { msalInstance } from '../../../index';

type GetTokenFn = () => Promise<string | null>;

/**
 * Default token getter using MSAL PublicClientApplication instance exported from `src/index.tsx`.
 * It first tries `acquireTokenSilent`, then falls back to `acquireTokenPopup`.
 *
 * A hoisted FUNCTION DECLARATION on purpose, not a const arrow: this module imports from
 * `src/index` (the app entry), so it sits inside a guaranteed import cycle — and
 * `createAxiosWithInterceptors` runs at module scope (every service's `new HttpService()`).
 * As a const, whichever dependency bump next reshuffles the cycle's evaluation order throws
 * "Cannot access 'defaultGetAccessToken' before initialization" (it did, on the 2026-08-07
 * lockfile refresh). A declaration hoists; the order can never matter again.
 */
export async function defaultGetAccessToken(): Promise<string | null> {
  // E2E test seam: when running under Playwright, bypass MSAL token acquisition and
  // return an injected/mock token. Guarded by REACT_APP_E2E so it is completely inert
  // in normal and production builds (the flag is only set for E2E test builds).
  if (process.env.REACT_APP_E2E === 'true') {
    try {
      const injected =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('E2E_ACCESS_TOKEN')
          : null;
      return injected ?? 'e2e-mock-token';
    } catch {
      return 'e2e-mock-token';
    }
  }

  try {
    const scopes = [
      `api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`,
      'User.Read',
    ];

    const silentRequest = {
      scopes,
    } as any;

    const result = await (msalInstance as any).acquireTokenSilent(silentRequest);
    return result?.accessToken ?? null;
  } catch (silentError) {
    try {
      const scopes = [
        `api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`,
        'User.Read',
      ];
      const interactiveResult = await (msalInstance as any).acquireTokenPopup({ scopes });
      return interactiveResult?.accessToken ?? null;
    } catch (interactiveError) {
      console.warn('Could not acquire token silently or via popup', interactiveError);
      return null;
    }
  }
}

/**
 * Installs request/response interceptors on the provided axios instance.
 * - Adds Authorization header using `getAccessToken`.
 * - Retries requests once after a 401 by obtaining a fresh token.
 * Returns a function to eject the interceptors.
 */
export function setupAxiosInterceptors(axiosInstance: AxiosInstance = axios, getAccessToken: GetTokenFn = defaultGetAccessToken) {
  const reqInterceptor = axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig & { headers?: AxiosRequestHeaders }) => {
      try {
        const token = await getAccessToken();
        const headers: AxiosRequestHeaders = (config.headers as AxiosRequestHeaders) || {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}` as any;
        }
        headers['Accept'] = 'application/json' as any;
        headers['API-key'] = process.env.REACT_APP_API_KEY;
        config.headers = headers as any;
        return config as any;
      } catch (err) {
        return config as any;
      }
    },
    (error) => Promise.reject(error)
  );

  const resInterceptor = axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError & { config?: any }) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);

  // If 401 and we haven't retried yet, attempt to refresh token and retry once
      if (error.response && error.response.status === 401 && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true;
        try {
          const token = await getAccessToken();
          if (token) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }
        } catch (e) {
          // fallthrough to reject
        }
      }

      // Normalize the error so callers can expect { message, status, data, errorCode }
      try {
        const respData = error.response?.data as any;
        const msg = respData?.error || respData?.message || error.message || 'Unknown error';
        const normalized: any = new Error(String(msg));
        normalized.status = error.response?.status;
        normalized.data = respData;
        if (respData?.errorCode) {
          normalized.errorCode = respData.errorCode;
        }
        return Promise.reject(normalized);
      } catch (e) {
        // Fallback to original error if normalization fails
        return Promise.reject(error);
      }
    }
  );

  return () => {
    axiosInstance.interceptors.request.eject(reqInterceptor);
    axiosInstance.interceptors.response.eject(resInterceptor);
  };
}

/**
 * Convenience: create a pre-configured axios instance and install interceptors.
 * Usage:
 * import { createAxiosWithInterceptors } from './services/interceptors/axiosInterceptors';
 * const api = createAxiosWithInterceptors({ baseURL: process.env.REACT_APP_API_URL });
 */
export function createAxiosWithInterceptors(config?: AxiosRequestConfig, getAccessToken?: GetTokenFn) {
  const instance = axios.create(config);
  setupAxiosInterceptors(instance, getAccessToken ?? defaultGetAccessToken);
  return instance;
}

export default setupAxiosInterceptors;
